import React, { useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';

import { Typography } from '@mui/material';

import Button from '@ui-elements/Button';
import SaveButton from '@ui-elements/SaveButton';
import InputWithLabel from '@ui-parts/InputWithLabel';
import BasicModal from '@ui-parts/BasicModal';
import QuotaListTable from '@ui-parts/QuotaListTable';
import DetailedConditionsDisplayArea from '@ui-parts/DetailedConditionsDisplayArea';
import InputValidationErrorTooltip from '@ui-parts/InputValidationErrorTooltip';

import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import { culcTotal, replaceFullToHalf } from '@functional';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { QUALIFICATION_GENDER_TYPE, LIMIT_TARGET_SAMPLES_TOTAL, LOCALE_STRING } from '~/constants';
import { FONT_WEIGHTS, FONT_SIZES, PALETTE, Z_INDEX } from '~/assets/styles/base';

import { callThunk, updatedTargetSamples } from '~/api';
import { targetSamplesValidationRule } from '~/constants/validationRules';

const BodyWrapper = styled.div`
  padding: 0 10px;
`;

const ConditionAreaWrapper = styled.div`
  margin-bottom: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 10px;
`;

const Title = styled.div`
  font-weight: ${FONT_WEIGHTS.size2};
`;

const ConditionTitle = styled.div`
  font-weight: ${FONT_WEIGHTS.size2};
  margin-bottom: 10px;
`;

const TargetSampleInputArea = styled.div`
  width: 270px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  // バリデーションエラーで表示被りが発生しないように調整
  .MuiTooltip-popper[data-popper-placement*='top'] .MuiTooltip-tooltip {
    margin-bottom: 0;
    div[color='error'] {
      padding: 7px 15px;
    }
  }
`;

const StyledButton = styled(Button)`
  &&&.MuiButton-root.MuiButton-contained {
    color: ${PALETTE.white};
    background-color: #5fb0f5;
    box-shadow: 1px 2px 2px #00000014;
    width: 47px;
  }
`;

const AlertWrapper = styled.div`
  position: relative;
  width: 398px;
  left: 50%;
`;

const OverSavedTargetSampleAlert = styled.div`
  box-shadow: 0px 3px 6px #00000029;
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.warning.main};
  padding: 10px 16px;
  font-size: ${FONT_SIZES.body3};
  position: absolute;
  transform: translate(-50%, -50%);
  top: -27px;
  z-index: ${Z_INDEX.base};
  display: flex;
  align-items: center;
  width: max-content;
  svg {
    font-size: 2.4rem;
    margin-right: 4px;
    color: ${PALETTE.warning.main};
  }
  :before,
  :after {
    content: '';
    border: solid transparent;
    pointer-events: none;
    position: absolute;
    left: 50%;
    transform: scale(1, -1);
  }
  :before {
    border: 7px solid transparent;
    border-bottom: 13px solid ${PALETTE.warning.main};
    margin-left: -7px;
    margin-bottom: 0;
    bottom: -20px;
  }
  :after {
    border: 6px solid transparent;
    border-bottom: 11px solid ${PALETTE.white};
    margin-left: -6px;
    bottom: -16px;
  }
`;

const CustomModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 71px;
  padding: 0 47px 0 29px;
`;

const TargetSamplesTotal = styled.span`
  font-weight: ${FONT_WEIGHTS.size2};
  font-size: 2.4rem;
  margin-left: 10px;
`;

const MinCollectZeroWarning = styled.div`
  border: 1px solid ${PALETTE.error.main};
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 8px 10px;
  margin: 15px 0 12px;
  width: 100%;
`;

const StyledTypography = styled(Typography)`
  svg {
    margin: 0 4px -2px 0;
  }
`;

function QuotaListTableModal({ open, handleClose, conditions }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // selector
  const { numberOfQuestions, targetSamplesTotal: surveyTargetSamplesTotal } = useSelector(
    (state) => state.survey
  );
  const quotasSlice = useSelector((state) => state.quotas);
  const qualificationsSlice = useSelector((state) => state.qualifications);

  const [ages, setAges] = useState({});
  const [quotas, setQuotas] = useState({});
  const [targetSamples, setTargetSamples] = useState({});
  const [targetSamplesTotal, setTargetSamplesTotal] = useState(0);
  const [targetSamplesWarnings, setTargetSamplesWarnings] = useState({});
  const [isShowTargetSamplesWarning, setIsShowTargetSamplesWarning] = useState(false);
  const [isShowMinCollectZeroWarning, setIsShowMinCollectZeroWarning] = useState(false);

  // 見積もり金額変更のアラート制御のため、保存前の金額と比較する
  useEffect(() => {
    const hasWarning = Object.values(targetSamples).some((targetSample, i) => {
      return Object.values(quotasSlice.targetSamples)[i] < targetSample;
    });
    setIsShowTargetSamplesWarning(hasWarning);
  }, [targetSamplesWarnings, quotasSlice.targetSamples]);

  useEffect(() => {
    const newTargetSamplesWarnings = {};
    Object.keys(targetSamples).forEach((key) => {
      newTargetSamplesWarnings[key] = quotasSlice.targetSamples[key] < targetSamples[key];
    });
    setTargetSamplesWarnings(newTargetSamplesWarnings);
    setTargetSamplesTotal(culcTotal({ items: targetSamples, isObject: true }));
  }, [targetSamples, quotasSlice.targetSamples]);

  // quota用のuseForm(バリデーション)
  const targetSamplesForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { ...targetSamples },
  });

  // 一括設定用のバリデーション
  const targetSamplesBulkForm = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { targetSamplesBulk: 0 },
  });

  useEffect(() => {
    setAges(quotasSlice.ages);
    setQuotas(quotasSlice.quotas);
    setTargetSamples(quotasSlice.targetSamples);
    // 提供可能数に0が含まれていればアラート出す
    setIsShowMinCollectZeroWarning(
      Object.keys(quotasSlice.targetSamples).some(
        (key) => quotasSlice.quotas[key]?.minCollect === 0
      )
    );
    targetSamplesForm.reset({ ...quotasSlice.targetSamples });
    targetSamplesBulkForm.reset({ targetSamplesBulk: 0 });
  }, [quotasSlice]);

  useEffect(() => {
    setTargetSamplesTotal(surveyTargetSamplesTotal);
  }, [surveyTargetSamplesTotal]);

  const width =
    qualificationsSlice.onSearched.gender === QUALIFICATION_GENDER_TYPE.EACH ? 950 : 700;

  const handleClickSetTargetSamples = () => {
    const newTargetSamples = Object.fromEntries(
      Object.keys(quotas).map((key) => {
        return [
          key,
          Number(replaceFullToHalf(targetSamplesBulkForm.getValues('targetSamplesBulk'))),
        ];
      })
    );
    setTargetSamples(newTargetSamples);
    // targetSamplesFormの値はresetしないと変更されない（setValueなどではだめ）。
    // また、useEffectでローカルステートのtargetSamplesの変更を検知してresetすると、
    // submit時のバリデーションエラーもリセットされてしまうため、ここでresetを行う。
    targetSamplesForm.reset({ ...newTargetSamples });
  };

  const validationOnClickSave = () => {
    // 回答希望数: 提供可能数が0の場合を除いて全て1以上か
    const errorIncludeZeroKeys = Object.keys(targetSamples)
      .map((key) => (targetSamples[key] === 0 && quotas[key].minCollect !== 0 ? key : false))
      .filter(Boolean);

    // 提供可能数が0の項目が含まれていないか
    const errorIsIncludeMinCollectZero = Object.keys(targetSamples)
      .map((key) => {
        if (quotas[key].minCollect === 0) {
          return key;
        }
        return false;
      })
      .filter(Boolean);

    // 提供可能数より少ないか
    const errorIsLargerThanMinCollectKeys = Object.keys(targetSamples)
      .map((key) => {
        if (targetSamples[key] > quotas[key].minCollect) {
          return key;
        }
        return false;
      })
      .filter(Boolean);

    // 回答希望数合計が、99999以下か
    const errorIsLargerThanTargetSamplesTotalMax = () => {
      return targetSamplesTotal > LIMIT_TARGET_SAMPLES_TOTAL.MAX;
    };

    if (
      errorIsIncludeMinCollectZero.length === 0 &&
      errorIsLargerThanMinCollectKeys.length === 0 &&
      errorIncludeZeroKeys.length === 0 &&
      !errorIsLargerThanTargetSamplesTotalMax()
    ) {
      return true;
    }

    if (errorIsLargerThanTargetSamplesTotalMax()) {
      targetSamplesForm.setError('targetSamplesTotal', {
        type: 'custom',
        message: t('validation.targetSampleTotalLessThanMax', {
          max: LIMIT_TARGET_SAMPLES_TOTAL.MAX.toLocaleString(LOCALE_STRING.ja),
        }),
      });
    }

    const errorKeys = [
      ...errorIsIncludeMinCollectZero,
      ...errorIsLargerThanMinCollectKeys,
      ...errorIncludeZeroKeys,
    ].sort();
    errorKeys.forEach((key, i) => {
      let message = '';
      if (errorIncludeZeroKeys.includes(key)) {
        message = t('validation.targetSamplesIncludeZero');
      } else if (errorIsIncludeMinCollectZero.includes(key)) {
        message = t('validation.minCollectZero');
      } else {
        message = t('validation.targetSampleIsLargerThanMinCollect');
      }
      targetSamplesForm.setError(key, {
        type: 'custom',
        message,
      });

      // 一番最初のエラー要素にフォーカス（MUIとuseFormの組み合わせでrefがうまく機能しないため、通常のjsで行う）
      if (i === 0) {
        const target = document.getElementsByName(key)[0];
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          target.focus();
        }, 300);
      }
    });
    return false;
  };

  const handleUpdatedTargetSamples = () => {
    if (!validationOnClickSave()) {
      return false;
    }
    dispatch(
      callThunk({
        action: updatedTargetSamples,
        payload: {
          targetSamples,
          numberOfQuestions,
        },
      })
    );
    handleClose();
    return true;
  };

  return (
    <BasicModal
      open={open}
      titleProps={{
        customModalTitle: [
          <CustomModalHeader key="targetSamplesDomProps">
            <Typography variant="title1">{t('quotas.conditions.titles.targetSamples')}</Typography>
            <InputValidationErrorTooltip
              error={Boolean(targetSamplesForm.formState?.errors?.targetSamplesTotal)}
              message={targetSamplesForm.formState?.errors?.targetSamplesTotal?.message}
              zIndex={Z_INDEX.MAX + 1}
              dataTestId="targetSamplesTotal-ErrorTooltip"
              isOneLinerMessage
            >
              <Typography variant="title2">
                {t('quotas.modals.quotas.caption')}
                <TargetSamplesTotal data-testid="TargetSamplesTotal">
                  {targetSamplesTotal.toLocaleString(LOCALE_STRING.ja)}
                </TargetSamplesTotal>
              </Typography>
            </InputValidationErrorTooltip>
          </CustomModalHeader>,
        ],
      }}
      handleClose={() => {
        handleClose();
        // 選択状態を元に戻してあげる
        setTargetSamples(quotasSlice.targetSamples);
        targetSamplesForm.reset({ ...quotasSlice.targetSamples });
        targetSamplesBulkForm.reset({ targetSamplesBulk: 0 });
      }}
      buttons={[
        <>
          <AlertWrapper hidden={!isShowTargetSamplesWarning}>
            <OverSavedTargetSampleAlert data-testid="OverSavedTargetSampleAlert">
              <NotificationImportantIcon />
              {t('quotas.changedTargetSamplesWarning')}
            </OverSavedTargetSampleAlert>
          </AlertWrapper>
          <SaveButton
            key="button1"
            sx={{ width: 148 }}
            handleClick={() => {
              // バリデーションをリセット (handleSubmit内のFnでリセットすると、2回目以降正しく動作しない)
              targetSamplesForm.clearErrors();
              return targetSamplesForm.handleSubmit(handleUpdatedTargetSamples)();
            }}
          />
        </>,
      ]}
      dataTestId="QuotaListTableModal"
      width={`${((width + 20) * 10) / 9 + 35}px`}
    >
      <BodyWrapper>
        <ConditionAreaWrapper>
          <ConditionTitle>{t('quotas.deliveryTargetCondition')}</ConditionTitle>
          <MinCollectZeroWarning
            hidden={!isShowMinCollectZeroWarning}
            data-testid="MinCollectZeroWarning"
          >
            <StyledTypography variant="body3">
              <WarningRoundedIcon />
              <span>{t('validation.minCollectsIncludeZero')}</span>
            </StyledTypography>
          </MinCollectZeroWarning>
          <DetailedConditionsDisplayArea conditions={conditions} />
        </ConditionAreaWrapper>

        <div>
          <Header>
            <Title>{t('quotas.inputTargetSamples')}</Title>
            <TargetSampleInputArea>
              <Typography variant="body3">{t('appCommon.bulkInput')}</Typography>
              <Controller
                name="targetSamplesBulk"
                control={targetSamplesBulkForm.control}
                rules={targetSamplesValidationRule}
                render={({
                  field: { onChange, onBlur, name, value, ref },
                  fieldState: { error },
                }) => {
                  return (
                    <InputValidationErrorTooltip
                      error={Boolean(error)}
                      message={error?.message}
                      zIndex={Z_INDEX.MAX + 1}
                      placement="top"
                      disablePortal
                      isOneLinerMessage
                      dataTestId="TargetSamplesBulkInput"
                    >
                      <div>
                        <InputWithLabel
                          name={name}
                          value={value}
                          width="100px"
                          textAlign="right"
                          handleChange={(e) => {
                            onChange(e);
                            targetSamplesBulkForm.setValue(
                              'targetSamplesBulk',
                              e.target.value.replace(/[^0-9０-９]+/i, '') // 全角と半角の数字のみ許可 Blurにて全角を半角に直す
                            );
                          }}
                          handleBlur={(e) => {
                            onBlur(e);
                            targetSamplesBulkForm.setValue(
                              'targetSamplesBulk',
                              Number(
                                replaceFullToHalf(
                                  targetSamplesBulkForm.getValues('targetSamplesBulk')
                                )
                              )
                            );
                          }}
                          dataTestId="TargetSamplesBulkInput"
                          inputProps={{ maxLength: 11 }}
                          ref={ref}
                        />
                      </div>
                    </InputValidationErrorTooltip>
                  );
                }}
              />

              <StyledButton
                handleClick={() => {
                  // バリデーションに渡す前に全角数字は半角に直す
                  targetSamplesBulkForm.setValue(
                    'targetSamplesBulk',
                    replaceFullToHalf(targetSamplesBulkForm.getValues('targetSamplesBulk'))
                  );
                  return targetSamplesBulkForm.handleSubmit(handleClickSetTargetSamples)();
                }}
                variant="contained"
                data-testid="SetTargetSamplesBulkButton"
              >
                {t('appCommon.setting')}
              </StyledButton>
            </TargetSampleInputArea>
          </Header>

          <QuotaListTable
            ages={ages}
            quotas={quotas}
            targetSamples={targetSamples}
            qualificationsGender={qualificationsSlice.onSearched.gender}
            canEdit
            setTargetSamples={setTargetSamples}
            minWidth={width}
            form={targetSamplesForm}
            targetSamplesWarnings={targetSamplesWarnings}
            setTargetSamplesWarnings={setTargetSamplesWarnings}
          />
        </div>
      </BodyWrapper>
    </BasicModal>
  );
}

QuotaListTableModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  conditions: PropTypes.shape({
    age: PropTypes.string,
    gender: PropTypes.string,
    region: PropTypes.string,
    job: PropTypes.string,
    industry: PropTypes.string,
    marriageStatus: PropTypes.string,
    childrenStatus: PropTypes.string,
  }),
};

export default memo(QuotaListTableModal);
